import api from "@/api/api-v1-config";
import { IUrl } from "@/interfaces/IUrl";

const url: IUrl = window as never;

// Define URLs
const URL_INSTANCES = url.urls.instances;
const URL_INSTANCE = url.urls.instance;
const URL_CREATE_INSTANCE = url.urls.createInstance;
const URL_DELETE_INSTANCE = url.urls.deleteInstance;
const URL_GET_QR = url.urls.getQr;
const URL_REBOOT_INSTANCE = url.urls.rebootInstance;
const URL_LOGOUT_INSTANCE = url.urls.logoutInstance;
const URL_SET_SETTINGS = url.urls.setSettings;
const URL_EVENT_LOG = url.urls.eventLog;
const URL_SEND_EVENT_LOG = url.urls.sendEventLog;
const URL_OPTION_LIST = url.urls.optionList;

/**
 * Возвращает список инстансов
 * @param payload
 */
/**
 * Запрашивает список инстансов с фильтрами, включая фильтр по каналу.
 */
export async function getInstancesAxios(payload: {
	page?: number;
	partnerId?: number;
	instanceId?: string;
	token?: string;
	phone?: string;
	statusId?: string;
	channel?: string;
	expireOrder?: string;
	insertOrder?: string;
}) {
	// Собираем query вручную по существующему стилю файла, добавляя channel к старым фильтрам.
	let page;
	let partnerId;
	let instanceId;
	let token;
	let phone;
	let statusId;
	let channel;
	let expireOrder;
	let insertOrder;

	// Page
	if (payload.page === undefined) {
		page = 1;
	} else {
		page = payload.page;
	}

	// Partner
	if (payload.partnerId === undefined) {
		partnerId = "";
	} else {
		partnerId = payload.partnerId;
	}

	// Instance
	if (payload.instanceId === undefined) {
		instanceId = "";
	} else {
		instanceId = payload.instanceId;
	}

	// Token
	if (payload.token === undefined) {
		token = "";
	} else {
		token = payload.token;
	}

	// Phone
	if (payload.phone === undefined) {
		phone = "";
	} else {
		phone = payload.phone;
	}

	// Status
	if (payload.statusId === undefined) {
		statusId = "";
	} else {
		statusId = payload.statusId;
	}

	// Канал показывает продукт инстанса: wapi, tapi или max.
	if (payload.channel === undefined) {
		channel = "";
	} else {
		channel = payload.channel;
	}

	// Expire order
	if (payload.expireOrder === undefined) {
		expireOrder = "";
	} else {
		expireOrder = payload.expireOrder;
	}

	// Insert order
	if (payload.insertOrder === undefined) {
		insertOrder = "";
	} else {
		insertOrder = payload.insertOrder;
	}

	return await api.get(
		URL_INSTANCES +
			"?page=" +
			page +
			"&partnerId=" +
			partnerId +
			"&instance=" +
			instanceId +
			"&token=" +
			token +
			"&phone=" +
			phone +
			"&statusId=" +
			statusId +
			"&channel=" +
			channel +
			"&expireOrder=" +
			expireOrder +
			"&insertOrder=" +
			insertOrder
	);
}

/**
 * Возвращает подробную информацию об инстансе
 * @param payload
 */
/**
 * Запрашивает детальную карточку инстанса с учетом канала.
 */
export async function getInstanceAxios(payload: {
	instanceId: number;
	channel?: string;
}) {
	// Без channel бэкенд ищет инстанс по всем источникам, но query убирает неоднозначность.
	const channel = payload.channel ? "?channel=" + payload.channel : "";

	return await api.get(URL_INSTANCE + "/" + payload.instanceId + channel);
}

/**
 * Создание инстанса
 */
export async function createInstanceAxios() {
	return await api.post(URL_CREATE_INSTANCE);
}

/**
 * Удаление инстанса
 * @param payload
 */
export async function deleteInstanceAxios(payload: {
	instance: number;
	// MAX API требует token инстанса при удалении, для WAPI/TAPI поле остается запасным.
	token?: string;
	channel?: string;
}) {
	return await api.post(URL_DELETE_INSTANCE, payload);
}

/**
 * Получить QR код
 * @param payload
 */
export async function qrCodeAxios(payload: {
	instance: number;
	token: string;
	// Канал нужен бэкенду, чтобы выбрать WAPI, TAPI или MAX QR endpoint.
	channel?: string;
}) {
	return await api.post(URL_GET_QR, payload);
}

/**
 * Reboot instance
 * @param payload
 */
export async function rebootInstanceAxios(payload: {
	instance: number;
	token: string;
	// Канал нужен бэкенду, чтобы вызвать правильный внешний сервис.
	channel?: string;
}) {
	return await api.post(URL_REBOOT_INSTANCE, payload);
}

/**
 * Logout instance
 * @param payload
 */
export async function logoutInstanceAxios(payload: {
	instance: number;
	token: string;
	// MAX называет метод logout иначе, поэтому выбор делается на бэкенде по каналу.
	channel?: string;
}) {
	return await api.post(URL_LOGOUT_INSTANCE, payload);
}

/**
 * Set settings
 * @param payload
 */
export async function setSettingsInstanceAxios(payload: {
	instance: number;
	token: string;
	// Настройки у каналов отличаются форматом, но фронт отправляет единый payload с channel.
	channel?: string;
	settings: object;
	field: string;
	oldValue: string;
	newValue: string;
}) {
	return await api.post(URL_SET_SETTINGS, payload);
}

/**
 * Лог действий
 * @param payload
 */
export async function getEventLogAxios(payload: { instanceId: number }) {
	return await api.get(URL_EVENT_LOG + "/" + payload.instanceId);
}

/**
 * Лог действий. Запись события
 * @param payload
 */
export async function sendEventLogAxios(payload: {
	eventLog: {
		instance: number;
		field: string;
		oldValue: string;
		newValue: string;
	};
}) {
	return await api.post(URL_SEND_EVENT_LOG, payload);
}

/**
 * Список опций для покупок
 */
export async function getOptionListAxios() {
	return await api.get(URL_OPTION_LIST);
}
